import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertCircle, Trash2, Loader2, Sparkles } from "lucide-react";
import { FULL_VERSION } from "@/config/version";
import { CHANGELOG, LATEST_VERSION } from "@/config/changelog";
import { clearAllCachesAndRefresh } from "@/utils/cacheUtils";
import { useState } from "react";

interface UpdateRequiredModalProps {
  open: boolean;
  isForced: boolean;
  onDismiss?: () => void;
  onOpenChange?: (open: boolean) => void;
}

export function UpdateRequiredModal({ open, isForced, onDismiss, onOpenChange }: UpdateRequiredModalProps) {
  const [isClearing, setIsClearing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Get latest changelog entry highlights (max 4)
  const latestChangelog = CHANGELOG[0];
  const highlights = latestChangelog?.highlights.slice(0, 4) || [];

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Mark as dismissed in session storage so it won't reappear after reload
    sessionStorage.setItem('wormble-update-dismissed-session', 'true');
    // Close modal immediately before reload
    onOpenChange?.(false);
    onDismiss?.();
    // Small delay to allow modal close animation
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleForceRefresh = async () => {
    setIsClearing(true);
    // Mark as dismissed in session storage so it won't reappear after reload
    sessionStorage.setItem('wormble-update-dismissed-session', 'true');
    // Close modal immediately
    onOpenChange?.(false);
    onDismiss?.();
    await clearAllCachesAndRefresh();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isForced) {
      onDismiss?.();
      onOpenChange?.(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            <AlertDialogTitle>Update to v{LATEST_VERSION}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3 text-left">
            <p>
              A new version of Wormble is available. Refresh now to get the latest features and improvements.
            </p>
            <div className="bg-muted p-3 rounded-md">
              <p className="text-xs text-muted-foreground">
                Current version: <span className="font-mono font-semibold">{FULL_VERSION}</span>
              </p>
            </div>
            
            {/* What's New Section */}
            {highlights.length > 0 && (
              <div className="bg-primary/5 border border-primary/20 p-3 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <p className="text-sm font-medium text-foreground">{"What's New"}</p>
                </div>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {highlights.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {isForced && (
              <p className="text-sm text-destructive font-medium">
                This is a critical update and must be installed to continue.
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {!isForced && onDismiss && (
            <Button variant="outline" onClick={onDismiss} className="w-full sm:w-auto">
              Remind Me Later
            </Button>
          )}
          <Button 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            className="w-full sm:w-auto gap-2"
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isRefreshing ? "Refreshing..." : "Refresh Now"}
          </Button>
          <Button 
            onClick={handleForceRefresh} 
            variant="secondary"
            disabled={isClearing}
            className="w-full sm:w-auto gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isClearing ? "Clearing..." : "Clear Cache & Refresh"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
